import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import LoadingSpinner from '../Loaders/LoadingSpinner';
import { IDefaultButtonProps, IDefaultButtonSize } from './types';
import { AnalyticsHtmlButton } from './AnalyticButton';

/**
 * This is a skeleton component for Buttons. Each styled button should provide
 * a list of css-variables in order to follow our styleguide
 *
 * @param size will change the size of the button
 * @param isGreyedOut will make your button look like disabled but clickable
 * @param isOutlined will add 1px border and transparent background
 * @param loading will show a spinner with a blurred backdrop on top
 */

const defaultButtonStyles = css`
  column-gap: 4px;
  min-height: 32px;
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
`;
const regularButtonStyles = css`
  column-gap: 2px;
  min-width: 64px;
  min-height: 24px;
  font-size: 12px;
  font-weight: 500;
  line-height: 22px;
`;
const smallButtonStyles = css`
  column-gap: 2px;
  min-height: 20px;
  font-size: 10px;
  font-weight: 500;
  line-height: normal;
`;
const disabledStyles = css`
  opacity: 0.3;
`;

const buttonSizeStylesLookup = {
  default: defaultButtonStyles,
  regular: regularButtonStyles,
  small: smallButtonStyles,
};

const StyledDefaultButton = styled(AnalyticsHtmlButton)<{
  $size: IDefaultButtonSize;
  $isOutlined: IDefaultButtonProps['isOutlined'];
  $isGreyedOut: IDefaultButtonProps['isGreyedOut'];
}>`
  flex: 0 0 auto;
  position: relative;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  padding: 0 8px;
  border: 1px solid currentColor;
  border-radius: 4px;
  text-align: center;
  font: inherit;
  outline: none;
  user-select: none;
  transition: all 0.1s ease-in-out;
  ${({ $size }) => buttonSizeStylesLookup[$size]};
  ${({ $isOutlined }) =>
    $isOutlined
      ? css`
          background: transparent;
          color: var(--button-text-c);
          &:not(:disabled):hover {
            color: var(--button-text-hover-c);
            cursor: pointer;
          }
        `
      : css`
          border-color: var(--button-bg-c);
          background: var(--button-bg-c);
          color: var(--button-text-c);
          &:not(:disabled):hover {
            border-color: var(--button-bg-hover-c);
            background: var(--button-bg-hover-c);
            color: var(--button-text-hover-c);
            cursor: pointer;
          }
        `};
  &:disabled {
    ${disabledStyles};
  }
  ${({ $isGreyedOut }) => $isGreyedOut && disabledStyles};
`;

const LoaderBackdrop = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  -webkit-backdrop-filter: blur(1px);
  backdrop-filter: blur(1px);
`;

const StyledButtonLoader = styled(LoadingSpinner)`
  &::before {
    border-color: var(--button-text-c) !important;
  }
`;

const DefaultButton = forwardRef<HTMLButtonElement, IDefaultButtonProps>(
  (
    {
      size = 'regular',
      isGreyedOut,
      isOutlined,
      loading,
      children,
      type = 'button',
      ...props
    },
    ref,
  ) => {
    return (
      <StyledDefaultButton
        {...props}
        ref={ref}
        type={type}
        $size={size}
        $isOutlined={isOutlined}
        $isGreyedOut={isGreyedOut}
      >
        {children}
        {loading && (
          <LoaderBackdrop>
            <StyledButtonLoader size="mini" active={true} />
          </LoaderBackdrop>
        )}
      </StyledDefaultButton>
    );
  },
);

export default DefaultButton;
